import './App.css';
import whiteArrow from './imgs/arrow-white.png';

/**
 * Renders a set of buttons based on the current question and user inputs.
 * 
 * @param {Object} props - The component props.
 * @param {Array} props.currentQuestion - The current question data.
 * @param {Object} props.dropdownVar - The selected dropdown values.
 * @param {Object} props.checkboxVar - The selected checkbox values.
 * @param {number} props.currentStep - The current step in the questionnaire.
 * @param {number} props.longevity - The longevity value for calculations.
 * @param {string} props.precedence - The precedence for visibility checks.
 * @param {boolean} props.isCheckboxChecked - Indicates if a checkbox is checked.
 * @param {string} props.id - The ID of the current component.
 * @param {Function} props.handleNextQuestion - Function to handle moving to the next question.
 * @returns {JSX.Element} A div containing the rendered buttons.
 */

function Button(props) {
  let listButtons;

  // Date Variables
  const oneYear = 31556926000;
  const currentDate = new Date();
  const totalTime = currentDate.getTime();
  const appointedDate = new Date(props.dropdownVar['appointed-date']).getTime();
  let equateDate = new Date(props.dropdownVar['equate-date']).getTime();
  if(isNaN(equateDate)){
      equateDate = new Date(appointedDate).getTime();
  }
  let termLength = props.dropdownVar['term-length'] * (oneYear / 2);
  if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse' || props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
    termLength = props.dropdownVar['term-length'] * oneYear;
  }
  const appointedDateTimeDifference = totalTime - appointedDate;
  const equateDateTimeDifference = totalTime - equateDate;
  const termLengthDifference = termLength + appointedDateTimeDifference;
  let maxYears = 7.5 * oneYear;
  if(props.jobTitle === 'school-psychologist' || props.jobTitle === 'social-worker'){
    maxYears = 6.5 * oneYear;
  }
  if(props.jobTitle === 'lab-tech'){
    maxYears = 5 * oneYear;
  }
  if(props.jobTitle === 'staff-nurse' || props.jobTitle === 'head-nurse' || props.jobTitle === 'supervisor-nurse'){
    maxYears = 20 * oneYear;
  }
  if(props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist'){
     maxYears = 10 * oneYear;
  }

  // Dynamicly set button value if linkId has multiple values
  let dynamicValue = (button) => {
    if(Array.isArray(button.linkId)){
      if(props.currentStep == 2){
        if(button.variableId == "term-length"){
          if(termLengthDifference > maxYears) {
            return button.linkId[0]
          }
          else{
            return button.linkId[1]
          }
        }
        else if(button.variableId[0] == ["equate-month"]){
          if(equateDate > appointedDate){
            return button.linkId[2]
          }
          else{
            if(equateDateTimeDifference > maxYears) {
              return button.linkId[0]
            }
            else{
              return button.linkId[1]
            }
          }
        }
        else if(button.variableId == "appointed-date" || button.variableId[0] == "appointed-month"){ 
          if(appointedDateTimeDifference > maxYears) {
            return button.linkId[0]
          }
          else{
            return button.linkId[1]
          }
        }
      }

      if(props.currentStep == 3){
        if(button.variableId == "appointed-date" || button.variableId[0] == "appointed-month"){ 
          if(props.longevity >= 1 && (props.jobTitle === 'occupational-therapist' || props.jobTitle === 'physical-therapist' || props.jobTitle === 'audiologist')){
            return button.linkId[2]
          }
          else if(props.longevity < 5) {
            return button.linkId[0]
          }
          if(props.longevity >= 22) {
            return button.linkId[1]
          }
          else{
            return button.linkId[2]
          }
        }
      }

    }
    else{
      return button.linkId;
    }
  }

  // Click event for button
  const handleClick = (event) => {
    {props.currentQuestion.map((variable) => {
      props.handleNextQuestion(event, props.id, variable.value);
      if(variable.variableId && variable.variableId.includes("degree-type")){
        props.handleDegreeType(props.currentId);
      }
    })}
  };

  // Shows and hide button
  let isButtonVisible = (button) => {
    if (button.hidden && button.variableId) {
      // Check checkboxes first
      if (props.checkboxVar && props.checkboxVar[button.variableId]) {
        const checkboxValues = props.checkboxVar[button.variableId];
        return Object.values(checkboxValues).some(value => value);
      }
      
      if (props.precedence == "checkbox" && props.isCheckboxChecked) {
        return false;
      }
  
      if (props.dropdownVar) {
        if (Array.isArray(button.variableId)) {
          // Save Appointed date
          if (button.variableId.includes('appointed-day') && button.variableId.includes('appointed-month') && button.variableId.includes('appointed-year')) {
            const day = props.dropdownVar['appointed-day'];
            const month = props.dropdownVar['appointed-month'];
            const year = props.dropdownVar['appointed-year'];
            
            if (day && month && year) {
              return true;
            }
            return false;
          }
          // Save Equate date
          else if(button.variableId.includes('equate-day') && button.variableId.includes('equate-month') && button.variableId.includes('equate-year')) {
            const day = props.dropdownVar['equate-day'];
            const month = props.dropdownVar['equate-month'];
            const year = props.dropdownVar['equate-year'];
            
            if (day && month && year) {
              return true;
            }
            return false;
          }
          else if(button.variableId.includes('hours-per-week')) {
            const hoursPerWeek = props.dropdownVar['hours-per-week'];
            
            if (hoursPerWeek) {
              return true;
            }
            return false;
          }
          
          // Check if all dropdowns in the array have values
          return button.variableId.every(id => 
            props.dropdownVar[id] && props.dropdownVar[id] !== ''
          );
        } else {
          // Single dropdown check
          return props.dropdownVar[button.variableId] && props.dropdownVar[button.variableId] !== '';
        }
      }
      
      // If neither checkbox nor dropdown has a value, keep the button hidden
      return false;
    }
    return true;
  };

  // Render buttons based on the current question
  const buttons = props.currentQuestion;
  if(buttons && Array.isArray(buttons)){
    listButtons = buttons.map((button, index) =>{
      return (
        <button className={button.linkStyle ? 'text-link' : `default-button next-button ${button.size} ${!isButtonVisible(button) ? 'hidden' : ''}`} id="scroll-to" data-current={props.currentId} value={dynamicValue(button)} data-varvalue={button.value} onClick={handleClick} key={index}>{button.label} {!button.linkStyle && <img src={whiteArrow} alt="" width="18" height="15" className="white-arrow"/>}</button>
      )
    })
  }

  return (
    <>
      <div className='button-wrapper'>
        {listButtons}
      </div>
    </>
  );
}

export default Button;